import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { humanizeAxiosError } from "@/lib/api";
import { Buildings, ShieldStar, IdentificationCard, Money, ChartBar, ClipboardText, Image as ImageIcon, Stamp, Eye, ClockCounterClockwise, Check, CaretUpDown } from "@phosphor-icons/react";
import { Link as RouterLink } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

export default function HRCenter() {
  const { user } = useAuth();
  const [stamp, setStamp] = useState(null);
  const [stampFile, setStampFile] = useState(null);
  const [autoStamp, setAutoStamp] = useState(false);
  const [tagline, setTagline] = useState({ eyebrow: "", title: "", subtitle: "", eyebrow_en: "", title_en: "", subtitle_en: "" });
  const [loginBg, setLoginBg] = useState(null);
  const [currentBg, setCurrentBg] = useState("");
  const [nameChanges, setNameChanges] = useState([]);
  const [salaryPending, setSalaryPending] = useState([]);
  const [surveyStats, setSurveyStats] = useState([]);
  const [rejectReq, setRejectReq] = useState(null); // {req, note}
  const [rejectNote, setRejectNote] = useState("");
  const [sigAuditUserId, setSigAuditUserId] = useState("");
  const [sigAudit, setSigAudit] = useState([]);
  const [personnel, setPersonnel] = useState([]);

  const canGlobalSettings = user?.role === "admin";
  const canStamp = ["admin", "hr", "gm"].includes(user?.role);
  const canApproveNames = ["admin", "hr"].includes(user?.role);
  const canSeeSurveys = ["admin", "hr", "gm"].includes(user?.role);
  const canSalary = ["admin", "hr"].includes(user?.role);
  const canIban = ["admin", "hr"].includes(user?.role);

  // IBAN management state
  const [ibanList, setIbanList] = useState([]);
  const [ibanSearch, setIbanSearch] = useState("");
  const [ibanEdit, setIbanEdit] = useState(null); // {id, first_name, last_name, iban, bank_name, account_no, branch_code, account_holder_name}
  const [ibanBulkFile, setIbanBulkFile] = useState(null);
  const [ibanBulkPreview, setIbanBulkPreview] = useState(null); // {updated, skipped, unmatched, dry_run}
  const [ibanBulkLoading, setIbanBulkLoading] = useState(false);
  // Tüm İzinler sekmesi
  const [alStart, setAlStart] = useState("");
  const [alEnd, setAlEnd] = useState("");
  const [alUser, setAlUser] = useState("");
  const [alUserOpen, setAlUserOpen] = useState(false);
  const [alType, setAlType] = useState("");
  const [alLeaves, setAlLeaves] = useState([]);
  const [alLoading, setAlLoading] = useState(false);
  const AL_TYPE_LABELS = { annual: "Yıllık", excuse: "Mazeret", sick: "Hastalık", report: "Raporlu", unpaid: "Ücretsiz", hourly: "Saatlik", maternity: "Doğum", marriage: "Evlilik", death: "Ölüm", birth: "Babalık" };
  const loadAllLeaves = async (allTime = false, userOverride = undefined, typeOverride = undefined) => {
    setAlLoading(true);
    try {
      const uid = userOverride !== undefined ? userOverride : alUser;
      const lt = typeOverride !== undefined ? typeOverride : alType;
      const p = new URLSearchParams();
      if (!allTime && alStart) p.append("start", alStart);
      if (!allTime && alEnd) p.append("end", alEnd);
      if (uid) p.append("user_id", uid);
      if (lt) p.append("leave_type", lt);
      const { data } = await api.get(`/leaves/all?${p.toString()}`);
      setAlLeaves(data.leaves || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "İzinler yüklenemedi");
    } finally {
      setAlLoading(false);
    }
  };
  const exportAllLeaves = async () => {
    try {
      const p = new URLSearchParams();
      if (alStart) p.append("start", alStart);
      if (alEnd) p.append("end", alEnd);
      if (alUser) p.append("user_id", alUser);
      if (alType) p.append("leave_type", alType);
      const r = await api.get(`/leaves/all/export?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "tum_izinler.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Excel indiriliyor");
    } catch { toast.error("Excel dışa aktarılamadı"); }
  };

  const loadIban = () => {
    if (!canIban) return;
    api.get("/personnel/iban/list").then((r) => setIbanList(r.data || [])).catch(() => {});
  };

  const load = () => {
    if (canStamp) {
      api.get("/settings/company-stamp").then((r) => setStamp(r.data)).catch(() => {});
      api.get("/settings/auto-stamp").then((r) => setAutoStamp(!!r.data?.enabled)).catch(() => {});
    }
    if (canGlobalSettings) {
      api.get("/settings/login-image").then((r) => {
        const u = r.data.url;
        setCurrentBg(u.startsWith("http") ? u : `${API}${u}`);
      }).catch(() => {});
      api.get("/settings/login-tagline").then((r) => setTagline(r.data || {})).catch(() => {});
    }
    if (canApproveNames) api.get("/personnel/name-change-requests").then((r) => setNameChanges(r.data)).catch(() => {});
    if (canSalary) api.get("/personnel/salary-pending").then((r) => setSalaryPending(r.data)).catch(() => {});
    if (canSeeSurveys) api.get("/reports/survey-stats").then((r) => setSurveyStats(r.data)).catch(() => {});
    api.get("/personnel/directory").then((r) => setPersonnel(r.data || [])).catch(() => {});
    if (canIban) api.get("/personnel/iban/list").then((r) => setIbanList(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.role]);

  const loadSigAudit = async (uid) => {
    setSigAuditUserId(uid);
    if (!uid) { setSigAudit([]); return; }
    try {
      const r = await api.get(`/signatures/audit/${uid}`);
      setSigAudit(r.data);
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  // --- Company stamp ---
  const uploadStamp = async () => {
    if (!stampFile) return;
    const fd = new FormData(); fd.append("file", stampFile);
    try {
      const { data } = await api.post("/settings/company-stamp", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStamp({ ...(stamp || {}), url: data.url });
      setStampFile(null);
      toast.success("Şirket kaşesi güncellendi");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };
  const toggleAutoStamp = async (v) => {
    try {
      await api.post("/settings/auto-stamp", { enabled: !!v });
      setAutoStamp(!!v);
      toast.success(v ? "Otomatik kaşe açık" : "Otomatik kaşe kapalı");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  // --- Login screen (admin) ---
  const uploadLoginBg = async () => {
    if (!loginBg) return;
    const fd = new FormData(); fd.append("file", loginBg);
    try {
      const { data } = await api.post(`/settings/login-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCurrentBg(data.url.startsWith("http") ? data.url : `${API}${data.url}`);
      setLoginBg(null);
      toast.success("Giriş ekranı görseli güncellendi");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };
  const saveTagline = async () => {
    try {
      await api.post("/settings/login-tagline", tagline);
      toast.success("Giriş metinleri güncellendi");
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  // --- Name changes ---
  const approveName = async (id) => {
    try {
      await api.post(`/personnel/name-change-requests/${id}/approve`);
      toast.success("Onaylandı — kullanıcı adı güncellendi");
      load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };
  const rejectNameSubmit = async () => {
    if (!rejectReq) return;
    try {
      await api.post(`/personnel/name-change-requests/${rejectReq.id}/reject`, { note: rejectNote });
      toast.success("Reddedildi");
      setRejectReq(null); setRejectNote("");
      load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  // --- Salary ---
  const approveSalary = async (uid) => {
    try {
      await api.post(`/personnel/${uid}/salary-approve`);
      toast.success("Maaş onaylandı");
      load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };
  const rejectSalary = async (uid) => {
    if (!window.confirm("Maaş talebini reddet?")) return;
    try {
      await api.post(`/personnel/${uid}/salary-reject`);
      toast.success("Reddedildi");
      load();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  // --- IBAN Management ---
  const saveIbanEdit = async () => {
    if (!ibanEdit?.id) return;
    try {
      const body = {
        iban: ibanEdit.iban || "",
        bank_name: ibanEdit.bank_name || "",
        account_no: ibanEdit.account_no || "",
        branch_code: ibanEdit.branch_code || "",
        account_holder_name: ibanEdit.account_holder_name || "",
      };
      await api.patch(`/personnel/${ibanEdit.id}/iban`, body);
      toast.success("IBAN bilgileri güncellendi");
      setIbanEdit(null);
      loadIban();
    } catch (e) { toast.error(humanizeAxiosError(e)); }
  };

  const downloadIbanTemplate = () => {
    const link = document.createElement("a");
    link.href = `${API}/api/personnel/iban/template`;
    link.setAttribute("download", "iban_sablonu.xlsx");
    // withCredentials for cookie auth
    fetch(link.href, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "iban_sablonu.xlsx";
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Şablon indirilemedi"));
  };

  const runIbanBulk = async (dryRun) => {
    if (!ibanBulkFile) { toast.error("Önce Excel dosyası seçin"); return; }
    setIbanBulkLoading(true);
    try {
      const fd = new FormData(); fd.append("file", ibanBulkFile);
      const { data } = await api.post(`/personnel/iban/bulk-upload?dry_run=${dryRun}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setIbanBulkPreview(data);
      if (!dryRun) {
        toast.success(`${data.updated.length} kayıt güncellendi`);
        loadIban();
      } else {
        toast.info(`Önizleme: ${data.updated.length} güncellenecek, ${data.unmatched.length} eşleşmedi`);
      }
    } catch (e) { toast.error(humanizeAxiosError(e)); }
    finally { setIbanBulkLoading(false); }
  };

  const filteredIban = useMemo(() => {
    const q = (ibanSearch || "").toLocaleLowerCase("tr");
    if (!q) return ibanList;
    return ibanList.filter((u) => {
      const s = `${u.first_name} ${u.last_name} ${u.email} ${u.department} ${u.iban} ${u.bank_name}`.toLocaleLowerCase("tr");
      return s.includes(q);
    });
  }, [ibanList, ibanSearch]);

  const stats = useMemo(() => ({
    stamp: stamp?.url ? 1 : 0,
    nameChanges: nameChanges.length,
    salary: salaryPending.length,
    surveys: surveyStats.length,
  }), [stamp, nameChanges, salaryPending, surveyStats]);

  return (
    <div className="space-y-6" data-testid="hr-center">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Yönetim</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-2">
            <ShieldStar size={28} weight="duotone" className="text-primary" />
            İK Kontrol Merkezi
          </h1>
          <div className="text-sm text-muted-foreground mt-1">Şirket kaşesi, giriş ekranı, isim değişiklikleri, maaş onayları ve anket istatistikleri tek panelde.</div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-muted-foreground">Şirket Kaşesi</div>
          <div className="font-display text-2xl font-bold mt-1">{stats.stamp ? "Aktif" : "Yok"}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Otomatik uygulama: {autoStamp ? "Açık" : "Kapalı"}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-muted-foreground">İsim Değişiklik</div>
          <div className="font-display text-2xl font-bold mt-1">{stats.nameChanges}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Onay bekleyen</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-muted-foreground">Maaş Talebi</div>
          <div className="font-display text-2xl font-bold mt-1">{stats.salary}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Onay bekleyen</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-muted-foreground">Anket</div>
          <div className="font-display text-2xl font-bold mt-1">{stats.surveys}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Toplam anket</div>
        </CardContent></Card>
      </div>

      <Tabs defaultValue={canApproveNames ? "names" : (canStamp ? "stamp" : "surveys")}>
        <TabsList className="flex-wrap h-auto">
          {canApproveNames && <TabsTrigger value="names" data-testid="tab-names">İsim Değişiklik ({stats.nameChanges})</TabsTrigger>}
          {canSalary && <TabsTrigger value="salary" data-testid="tab-salary">Maaş Onayı ({stats.salary})</TabsTrigger>}
          {canIban && <TabsTrigger value="iban" data-testid="tab-iban">IBAN Yönetimi</TabsTrigger>}
          {canStamp && <TabsTrigger value="stamp" data-testid="tab-stamp">Şirket Kaşesi</TabsTrigger>}
          {canGlobalSettings && <TabsTrigger value="login" data-testid="tab-login">Giriş Ekranı</TabsTrigger>}
          {canSeeSurveys && <TabsTrigger value="surveys" data-testid="tab-surveys">Anket İstatistikleri</TabsTrigger>}
          <TabsTrigger value="sig-audit" data-testid="tab-sig-audit">İmza Denetim Kayıtları</TabsTrigger>
          <TabsTrigger value="all-leaves" data-testid="tab-all-leaves">Tüm İzinler</TabsTrigger>
          <TabsTrigger value="quick" data-testid="tab-quick">Kısayollar</TabsTrigger>
        </TabsList>

        {canApproveNames && (
          <TabsContent value="names">
            <Card><CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr><th className="text-left p-3">Mevcut Ad Soyad</th><th className="text-left p-3">Talep Edilen</th><th className="text-left p-3">Gerekçe</th><th className="text-left p-3">Tarih</th><th className="text-left p-3">İşlem</th></tr>
                  </thead>
                  <tbody>
                    {nameChanges.map((n) => (
                      <tr key={n.id} className="border-t border-border">
                        <td className="p-3">{n.user_current_name}</td>
                        <td className="p-3">
                          <div>{n.requested_first_name || "-"} {n.requested_last_name || ""}</div>
                        </td>
                        <td className="p-3 text-xs text-muted-foreground max-w-xs">{n.reason || "-"}</td>
                        <td className="p-3 text-xs">{n.at?.slice(0, 10)}</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => approveName(n.id)} data-testid={`name-approve-${n.id}`}>Onayla</Button>
                            <Button size="sm" variant="outline" onClick={() => { setRejectReq(n); setRejectNote(""); }} data-testid={`name-reject-${n.id}`}>Reddet</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {nameChanges.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Bekleyen talep yok</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent></Card>
          </TabsContent>
        )}

        {canSalary && (
          <TabsContent value="salary">
            <Card><CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr><th className="text-left p-3">Personel</th><th className="text-left p-3">Departman</th><th className="text-left p-3">Pozisyon</th><th className="text-left p-3">Mevcut</th><th className="text-left p-3">Talep</th><th className="text-left p-3">Tarih</th><th className="text-left p-3">İşlem</th></tr>
                  </thead>
                  <tbody>
                    {salaryPending.map((s) => (
                      <tr key={s.id} className="border-t border-border">
                        <td className="p-3">{s.name}</td>
                        <td className="p-3">{s.department}</td>
                        <td className="p-3">{s.position}</td>
                        <td className="p-3">{Number(s.current_salary || 0).toLocaleString("tr-TR")} ₺</td>
                        <td className="p-3 font-semibold">{Number(s.requested_salary || 0).toLocaleString("tr-TR")} ₺</td>
                        <td className="p-3 text-xs">{s.requested_at?.slice(0, 10)}</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => approveSalary(s.id)} data-testid={`salary-approve-${s.id}`}>Onayla</Button>
                            <Button size="sm" variant="outline" onClick={() => rejectSalary(s.id)} data-testid={`salary-reject-${s.id}`}>Reddet</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {salaryPending.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Bekleyen talep yok</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent></Card>
          </TabsContent>
        )}

        {canIban && (
          <TabsContent value="iban">
            <div className="grid lg:grid-cols-5 gap-4">
              <Card className="lg:col-span-3"><CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-display font-bold text-lg">Personel Banka & IBAN Bilgileri</div>
                  <Badge variant="secondary" data-testid="iban-count">{ibanList.length} personel</Badge>
                </div>
                <Input placeholder="Ara: ad, e-posta, departman, IBAN…" value={ibanSearch} onChange={(e) => setIbanSearch(e.target.value)} data-testid="iban-search" />
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="text-left p-2">Personel</th>
                        <th className="text-left p-2">Departman</th>
                        <th className="text-left p-2">Banka</th>
                        <th className="text-left p-2">IBAN</th>
                        <th className="text-left p-2">Hesap No / Şube</th>
                        <th className="text-left p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredIban.map((u) => (
                        <tr key={u.id} className="border-t border-border align-top" data-testid={`iban-row-${u.id}`}>
                          <td className="p-2">
                            <div className="font-medium">{u.first_name} {u.last_name}</div>
                            <div className="text-[11px] text-muted-foreground">{u.email}</div>
                          </td>
                          <td className="p-2 text-xs">{u.department || "-"}</td>
                          <td className="p-2 text-xs">{u.bank_name || <span className="text-muted-foreground italic">boş</span>}</td>
                          <td className="p-2 font-mono text-[12px] break-all">
                            {u.iban ? u.iban.replace(/(.{4})/g, "$1 ").trim() : <span className="text-muted-foreground italic">tanımlanmamış</span>}
                          </td>
                          <td className="p-2 text-xs">
                            <div>{u.account_no || "-"}</div>
                            <div className="text-muted-foreground">Şb: {u.branch_code || "-"}</div>
                          </td>
                          <td className="p-2">
                            <Button size="sm" variant="outline" onClick={() => setIbanEdit({ ...u })} data-testid={`iban-edit-${u.id}`}>Düzenle</Button>
                          </td>
                        </tr>
                      ))}
                      {filteredIban.length === 0 && (
                        <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">Kayıt bulunamadı</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent></Card>

              <Card className="lg:col-span-2"><CardContent className="p-4 space-y-3">
                <div className="font-display font-bold text-lg">Toplu IBAN Yükleme</div>
                <p className="text-xs text-muted-foreground">
                  Excel şablonunu indir, IBAN sütununu doldur, yükle. Eşleşme <b>e-posta</b> (öncelikli) veya <b>TC kimlik</b> ile yapılır.
                </p>
                <Button variant="outline" size="sm" onClick={downloadIbanTemplate} data-testid="iban-download-template">
                  Şablon İndir (Excel)
                </Button>
                <div className="border-t border-border pt-3 space-y-2">
                  <Label>Doldurulmuş Excel Dosyası</Label>
                  <Input type="file" accept=".xlsx,.xls" onChange={(e) => { setIbanBulkFile(e.target.files?.[0]); setIbanBulkPreview(null); }} data-testid="iban-bulk-file" />
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" size="sm" disabled={!ibanBulkFile || ibanBulkLoading} onClick={() => runIbanBulk(true)} data-testid="iban-bulk-preview">
                      Önizle
                    </Button>
                    <Button size="sm" disabled={!ibanBulkFile || ibanBulkLoading} onClick={() => runIbanBulk(false)} data-testid="iban-bulk-apply">
                      Yükle & Kaydet
                    </Button>
                  </div>
                </div>
                {ibanBulkPreview && (
                  <div className="border-t border-border pt-3 space-y-2 text-xs" data-testid="iban-bulk-result">
                    <div className="flex gap-2 flex-wrap">
                      <Badge>Güncellenen: {ibanBulkPreview.updated.length}</Badge>
                      <Badge variant="secondary">Atlanan: {ibanBulkPreview.skipped.length}</Badge>
                      <Badge variant="destructive">Eşleşmeyen: {ibanBulkPreview.unmatched.length}</Badge>
                    </div>
                    {ibanBulkPreview.updated.length > 0 && (
                      <div>
                        <div className="font-semibold mb-1">Güncellenecek/Güncellenen:</div>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {ibanBulkPreview.updated.slice(0, 20).map((u, i) => (
                            <div key={i} className="rounded-sm bg-muted/40 p-1.5">
                              <div className="font-medium">{u.name} <span className="text-muted-foreground">— {u.email}</span></div>
                              <div className="font-mono text-[11px]">{u.iban}</div>
                            </div>
                          ))}
                          {ibanBulkPreview.updated.length > 20 && (
                            <div className="text-muted-foreground text-center">+{ibanBulkPreview.updated.length - 20} kayıt daha…</div>
                          )}
                        </div>
                      </div>
                    )}
                    {ibanBulkPreview.unmatched.length > 0 && (
                      <div>
                        <div className="font-semibold mb-1 text-destructive">Eşleşmeyen kayıtlar:</div>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {ibanBulkPreview.unmatched.map((u, i) => (
                            <div key={i} className="text-muted-foreground">
                              {u.email || u.tc || "?"}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent></Card>
            </div>
          </TabsContent>
        )}

        {canStamp && (
          <TabsContent value="stamp">
            <Card><CardContent className="p-6 space-y-4">
              <div className="font-display font-bold text-lg flex items-center gap-2"><Stamp size={20} />Şirket Kaşesi</div>
              <div className="text-sm text-muted-foreground">
                Bordroya kaşe basmak için bir kaşe görseli yükleyin.
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {stamp?.url && (
                  <div className="rounded-md bg-white border p-3">
                    <img src={stamp.url.startsWith("http") ? stamp.url : `${API}${stamp.url}`} alt="kaşe" className="h-24 object-contain" data-testid="stamp-preview" />
                    {stamp.uploaded_by_name && <div className="text-[10px] text-muted-foreground mt-1">Yükleyen: {stamp.uploaded_by_name}</div>}
                  </div>
                )}
                <div className="flex-1 min-w-[240px]">
                  <Label>Kaşe Görseli (JPG/PNG/WEBP)</Label>
                  <Input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setStampFile(e.target.files?.[0])} data-testid="hr-stamp-file" />
                </div>
                <Button onClick={uploadStamp} disabled={!stampFile} data-testid="hr-stamp-upload">Yükle</Button>
              </div>
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 p-3">
                <Switch checked={autoStamp} onCheckedChange={toggleAutoStamp} data-testid="hr-auto-stamp" />
                <div>
                  <div className="text-sm font-medium">Personel imzaladığında bordroya otomatik kaşe uygula</div>
                  <div className="text-xs text-muted-foreground">Açıkken: bir personel bordroyu onayladığı anda kaşe otomatik uygulanır.</div>
                </div>
              </div>
            </CardContent></Card>
          </TabsContent>
        )}

        {canGlobalSettings && (
          <TabsContent value="login">
            <div className="grid lg:grid-cols-2 gap-4">
              <Card><CardContent className="p-6 space-y-3">
                <div className="font-display font-bold text-lg flex items-center gap-2"><ImageIcon size={20} />Giriş Ekranı Görseli</div>
                {currentBg && (
                  <div className="rounded-md overflow-hidden border border-border">
                    <img src={currentBg} alt="mevcut" className="w-full h-40 object-cover" />
                  </div>
                )}
                <Input type="file" accept="image/*" onChange={(e) => setLoginBg(e.target.files?.[0])} data-testid="hr-login-bg" />
                <Button variant="outline" onClick={uploadLoginBg} disabled={!loginBg} data-testid="hr-login-bg-upload">Yükle</Button>
              </CardContent></Card>

              <Card><CardContent className="p-6 space-y-3">
                <div className="font-display font-bold text-lg flex items-center gap-2"><Buildings size={20} />Giriş Ekranı Metinleri</div>
                <div className="rounded-md border border-border p-3 space-y-2">
                  <div className="text-xs uppercase font-semibold text-muted-foreground">Türkçe</div>
                  <Input placeholder="Üst etiket" value={tagline.eyebrow || ""} onChange={(e) => setTagline({...tagline, eyebrow: e.target.value})} data-testid="hr-tag-eb" />
                  <Input placeholder="Başlık" value={tagline.title || ""} onChange={(e) => setTagline({...tagline, title: e.target.value})} data-testid="hr-tag-t" />
                  <Input placeholder="Alt metin" value={tagline.subtitle || ""} onChange={(e) => setTagline({...tagline, subtitle: e.target.value})} data-testid="hr-tag-s" />
                </div>
                <div className="rounded-md border border-border p-3 space-y-2">
                  <div className="text-xs uppercase font-semibold text-muted-foreground">English</div>
                  <Input placeholder="Eyebrow" value={tagline.eyebrow_en || ""} onChange={(e) => setTagline({...tagline, eyebrow_en: e.target.value})} data-testid="hr-tag-eb-en" />
                  <Input placeholder="Title" value={tagline.title_en || ""} onChange={(e) => setTagline({...tagline, title_en: e.target.value})} data-testid="hr-tag-t-en" />
                  <Input placeholder="Subtitle" value={tagline.subtitle_en || ""} onChange={(e) => setTagline({...tagline, subtitle_en: e.target.value})} data-testid="hr-tag-s-en" />
                </div>
                <Button onClick={saveTagline} data-testid="hr-tag-save">Kaydet</Button>
              </CardContent></Card>
            </div>
          </TabsContent>
        )}

        {canSeeSurveys && (
          <TabsContent value="surveys">
            <div className="space-y-4">
              {surveyStats.length === 0 && (
                <Card><CardContent className="p-6 text-center text-muted-foreground text-sm">Henüz anket bulunmuyor.</CardContent></Card>
              )}
              {surveyStats.map((s) => (
                <Card key={s.survey_id}><CardContent className="p-6 space-y-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-display font-bold text-lg">{s.title}</div>
                    <Badge>{s.total_responses} yanıt</Badge>
                  </div>
                  <div className="space-y-4">
                    {s.questions.map((q, qi) => {
                      const total = q.count || 1;
                      return (
                        <div key={qi} className="border-t border-border pt-3">
                          <div className="text-sm font-semibold mb-2">{q.question}</div>
                          <div className="space-y-1.5">
                            {Object.entries(q.distribution || {}).map(([k, v]) => {
                              const pct = Math.round((v / total) * 100);
                              return (
                                <div key={k} className="flex items-center gap-2 text-xs">
                                  <div className="w-16 text-muted-foreground truncate">{k}</div>
                                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                    <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                                  </div>
                                  <div className="w-16 text-right">{v} <span className="text-muted-foreground">({pct}%)</span></div>
                                </div>
                              );
                            })}
                            {Object.keys(q.distribution || {}).length === 0 && <div className="text-xs text-muted-foreground">Bu soru için istatistik hesaplanamıyor (açık uçlu).</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent></Card>
              ))}
            </div>
          </TabsContent>
        )}

        <TabsContent value="sig-audit">
          <Card><CardContent className="p-6 space-y-4">
            <div className="font-display font-bold text-lg flex items-center gap-2"><Eye size={20} />İmza Erişim Kayıtları</div>
            <div className="text-sm text-muted-foreground">
              Personel imzalarına kimin, ne zaman ve hangi bağlamda eriştiğini görmek için personel seçin. İmza görüntülemek için istek atıldığında (5 dakikalık signed URL) audit log'a otomatik eklenir.
            </div>
            <div className="max-w-md">
              <Label>Personel</Label>
              <Select value={sigAuditUserId} onValueChange={loadSigAudit}>
                <SelectTrigger data-testid="sig-audit-user"><SelectValue placeholder="Personel seçin" /></SelectTrigger>
                <SelectContent>
                  {personnel
                    .slice()
                    .sort((a, b) => (a.first_name || "").localeCompare(b.first_name || ""))
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} — {p.department}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>
            {sigAuditUserId && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr><th className="text-left p-3">Tarih/Saat</th><th className="text-left p-3">Görüntüleyen</th><th className="text-left p-3">Rol</th><th className="text-left p-3">Bağlam</th></tr>
                  </thead>
                  <tbody>
                    {sigAudit.map((r, i) => (
                      <tr key={r.id || i} className="border-t border-border">
                        <td className="p-3 font-mono text-xs">{r.at?.slice(0, 19).replace("T", " ")}</td>
                        <td className="p-3">{r.actor || "-"}</td>
                        <td className="p-3"><Badge variant="secondary">{r.role}</Badge></td>
                        <td className="p-3 text-xs text-muted-foreground">{r.meta?.context || "-"}</td>
                      </tr>
                    ))}
                    {sigAudit.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Bu personelin imzasına erişim kaydı yok.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="all-leaves">
          <Card><CardContent className="p-6 space-y-4">
            <div>
              <h3 className="font-semibold">Tüm Personel İzinleri</h3>
              <p className="text-sm text-muted-foreground">Programa kayıtlı tüm personelin onaylı izinleri. Tarih aralığı seçin veya tüm zamanları getirin.</p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs uppercase text-muted-foreground block mb-1">Başlangıç</label>
                <Input type="date" value={alStart} onChange={(e) => setAlStart(e.target.value)} data-testid="al-start" className="w-40" />
              </div>
              <div>
                <label className="text-xs uppercase text-muted-foreground block mb-1">Bitiş</label>
                <Input type="date" value={alEnd} onChange={(e) => setAlEnd(e.target.value)} data-testid="al-end" className="w-40" />
              </div>
              <div className="min-w-[220px]">
                <label className="text-xs uppercase text-muted-foreground block mb-1">Personel</label>
                <Popover open={alUserOpen} onOpenChange={setAlUserOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={alUserOpen} data-testid="al-user" className="w-56 justify-between font-normal">
                      <span className="truncate">
                        {alUser ? (() => { const p = personnel.find((x) => x.id === alUser); return p ? `${p.first_name} ${p.last_name}` : "Seçili personel"; })() : "Tümü"}
                      </span>
                      <CaretUpDown size={16} className="ml-2 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Personel ara..." data-testid="al-user-search" />
                      <CommandList>
                        <CommandEmpty>Personel bulunamadı.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem value="Tümü" data-testid="al-user-opt-all" onSelect={() => { setAlUser(""); setAlUserOpen(false); loadAllLeaves(false, ""); }}>
                            <Check size={16} className={`mr-2 ${!alUser ? "opacity-100" : "opacity-0"}`} />Tümü
                          </CommandItem>
                          {personnel.map((p) => (
                            <CommandItem key={p.id} value={`${p.first_name} ${p.last_name} ${p.department || ""}`} data-testid={`al-user-opt-${p.id}`} onSelect={() => { setAlUser(p.id); setAlUserOpen(false); loadAllLeaves(false, p.id); }}>
                              <Check size={16} className={`mr-2 ${alUser === p.id ? "opacity-100" : "opacity-0"}`} />
                              <span className="truncate">{p.first_name} {p.last_name}{p.department ? ` · ${p.department}` : ""}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="min-w-[160px]">
                <label className="text-xs uppercase text-muted-foreground block mb-1">İzin Türü</label>
                <Select value={alType || "all"} onValueChange={(v) => { const nv = v === "all" ? "" : v; setAlType(nv); loadAllLeaves(!alStart && !alEnd, undefined, nv); }}>
                  <SelectTrigger data-testid="al-type" className="w-40"><SelectValue placeholder="Tümü" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tümü</SelectItem>
                    {Object.entries(AL_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => loadAllLeaves(false)} data-testid="al-apply">Filtrele</Button>
              <Button variant="outline" onClick={() => { setAlStart(""); setAlEnd(""); loadAllLeaves(true); }} data-testid="al-all-time">Tüm Zamanlar</Button>
              <Button variant="outline" onClick={exportAllLeaves} data-testid="al-export" disabled={alLeaves.length === 0}>Excel'e Aktar</Button>
            </div>
            {alLoading ? (
              <div className="text-sm text-muted-foreground">Yükleniyor…</div>
            ) : alLeaves.length === 0 ? (
              <div className="text-sm text-muted-foreground">Sonuç yok. Filtre uygulayın veya "Tüm Zamanlar"a tıklayın.</div>
            ) : (
              <div className="overflow-x-auto">
                <div className="text-xs text-muted-foreground mb-2" data-testid="al-count">{alLeaves.length} izin kaydı</div>
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left py-1">Personel</th>
                      <th className="text-left py-1">Departman</th>
                      <th className="text-left py-1">Tür</th>
                      <th className="text-left py-1">Başlangıç</th>
                      <th className="text-left py-1">Bitiş</th>
                      <th className="text-left py-1">Gün</th>
                      <th className="text-left py-1">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alLeaves.map((lv) => (
                      <tr key={lv.id} className="border-t border-border" data-testid={`al-row-${lv.id}`}>
                        <td className="py-1">{lv.user_name}</td>
                        <td className="py-1">{lv.department}</td>
                        <td className="py-1">{AL_TYPE_LABELS[lv.leave_type] || lv.leave_type}{lv.leave_type === "hourly" && lv.start_time ? ` (${lv.start_time}-${lv.end_time})` : ""}</td>
                        <td className="py-1">{lv.start_date}</td>
                        <td className="py-1">{lv.end_date}</td>
                        <td className="py-1">{lv.day_count}</td>
                        <td className="py-1 max-w-[220px] truncate" title={lv.reason}>{lv.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="quick">
          <Card><CardContent className="p-6 space-y-3">
            <div className="font-display font-bold text-lg flex items-center gap-2"><ChartBar size={20} />Kısayollar</div>
            <div className="grid md:grid-cols-2 gap-3">
              <RouterLink to="/reports/monthly-leaves" className="rounded-md border border-border hover:border-primary p-4 transition-colors group" data-testid="quick-monthly-leaves">
                <div className="flex items-center gap-2 font-semibold group-hover:text-primary"><ClockCounterClockwise size={18} />Aylık İzin Raporu</div>
                <div className="text-xs text-muted-foreground mt-1">Departman departman alfabetik sıralı, Excel indirilebilir.</div>
              </RouterLink>
              <RouterLink to="/pending" className="rounded-md border border-border hover:border-primary p-4 transition-colors group" data-testid="quick-pending">
                <div className="flex items-center gap-2 font-semibold group-hover:text-primary"><IdentificationCard size={18} />Bekleyen Kayıtlar</div>
                <div className="text-xs text-muted-foreground mt-1">Yeni personel kayıt onayları.</div>
              </RouterLink>
              <RouterLink to="/audit" className="rounded-md border border-border hover:border-primary p-4 transition-colors group" data-testid="quick-audit">
                <div className="flex items-center gap-2 font-semibold group-hover:text-primary"><ClipboardText size={18} />Denetim Günlüğü</div>
                <div className="text-xs text-muted-foreground mt-1">Tüm sistem işlemleri.</div>
              </RouterLink>
              <RouterLink to="/payroll-admin" className="rounded-md border border-border hover:border-primary p-4 transition-colors group" data-testid="quick-payroll">
                <div className="flex items-center gap-2 font-semibold group-hover:text-primary"><Money size={18} />Bordro Onay Takibi</div>
                <div className="text-xs text-muted-foreground mt-1">Aylık bakış + memnuniyet + PDF sağlık.</div>
              </RouterLink>
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
      <Dialog open={!!rejectReq} onOpenChange={(o) => !o && setRejectReq(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>İsim Değişiklik Talebini Reddet</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">
            {rejectReq?.user_current_name} — {rejectReq?.requested_first_name} {rejectReq?.requested_last_name}
          </div>
          <Textarea placeholder="Ret gerekçesi (opsiyonel)" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} data-testid="name-reject-note" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setRejectReq(null)}>Vazgeç</Button>
            <Button variant="destructive" onClick={rejectNameSubmit} data-testid="name-reject-submit">Reddet</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!ibanEdit} onOpenChange={(o) => !o && setIbanEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>IBAN & Banka Bilgileri</DialogTitle></DialogHeader>
          {ibanEdit && (
            <div className="space-y-3">
              <div className="rounded-md bg-muted/40 p-2 text-sm">
                <div className="font-medium">{ibanEdit.first_name} {ibanEdit.last_name}</div>
                <div className="text-xs text-muted-foreground">{ibanEdit.email} · {ibanEdit.department}</div>
              </div>
              <div>
                <Label>IBAN</Label>
                <Input value={ibanEdit.iban || ""} onChange={(e) => setIbanEdit({ ...ibanEdit, iban: e.target.value })} placeholder="TR33 0006 4000 0011 2345 6789 01" data-testid="iban-edit-iban" className="font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Banka Adı</Label>
                  <Input value={ibanEdit.bank_name || ""} onChange={(e) => setIbanEdit({ ...ibanEdit, bank_name: e.target.value })} data-testid="iban-edit-bank" />
                </div>
                <div>
                  <Label>Hesap Sahibi Adı</Label>
                  <Input value={ibanEdit.account_holder_name || ""} onChange={(e) => setIbanEdit({ ...ibanEdit, account_holder_name: e.target.value })} data-testid="iban-edit-holder" />
                </div>
                <div>
                  <Label>Hesap Numarası</Label>
                  <Input value={ibanEdit.account_no || ""} onChange={(e) => setIbanEdit({ ...ibanEdit, account_no: e.target.value })} data-testid="iban-edit-accno" />
                </div>
                <div>
                  <Label>Şube Kodu</Label>
                  <Input value={ibanEdit.branch_code || ""} onChange={(e) => setIbanEdit({ ...ibanEdit, branch_code: e.target.value })} data-testid="iban-edit-branch" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setIbanEdit(null)}>Vazgeç</Button>
                <Button onClick={saveIbanEdit} data-testid="iban-edit-save">Kaydet</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
